import React, { useState, useMemo } from 'react';
import { Building2, Users, Plus, Eye, Edit, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import type { TravelAgency, TravelAgencyMaintenanceState, TravelAgencyPaginationInfo } from '../../../../types/travelAgencyMaintenance';
import { mockTravelAgencies } from '../../../../data/mockTravelAgencies';
import TravelAgencyDetailsModal from './components/TravelAgencyDetailsModal';
import '../../../../styles/travelAgencyMaintenance.css';

interface TravelAgencyMaintenanceProps {
  className?: string;
  onEditTravelAgency?: (travelAgency: TravelAgency) => void;
}

const TravelAgencyMaintenance: React.FC<TravelAgencyMaintenanceProps> = ({ className = '', onEditTravelAgency }) => {
  const [state, setState] = useState<TravelAgencyMaintenanceState>({
    travelAgencies: mockTravelAgencies,
    searchTerm: '',
    currentPage: 1,
    itemsPerPage: 5,
    totalTravelAgencies: mockTravelAgencies.length,
    selectedTravelAgencies: [],
    isLoading: false
  });

  const [viewDetailsModal, setViewDetailsModal] = useState<{
    isOpen: boolean;
    travelAgency: TravelAgency | null;
  }>({ isOpen: false, travelAgency: null });

  // Filter travel agencies based on search term
  const filteredTravelAgencies = useMemo(() => {
    if (!state.searchTerm) {
      return state.travelAgencies;
    }

    const searchLower = state.searchTerm.toLowerCase();
    return state.travelAgencies.filter(ta =>
      ta.taCode.toLowerCase().includes(searchLower) ||
      ta.taName.toLowerCase().includes(searchLower)
    );
  }, [state.travelAgencies, state.searchTerm]);

  // Calculate pagination
  const paginationInfo = useMemo((): TravelAgencyPaginationInfo => {
    const totalTravelAgencies = filteredTravelAgencies.length;
    const totalPages = Math.ceil(totalTravelAgencies / state.itemsPerPage);
    const startTravelAgency = (state.currentPage - 1) * state.itemsPerPage + 1;
    const endTravelAgency = Math.min(state.currentPage * state.itemsPerPage, totalTravelAgencies);

    return {
      currentPage: state.currentPage,
      totalPages,
      totalTravelAgencies,
      itemsPerPage: state.itemsPerPage,
      startTravelAgency: totalTravelAgencies > 0 ? startTravelAgency : 0,
      endTravelAgency: totalTravelAgencies > 0 ? endTravelAgency : 0
    };
  }, [filteredTravelAgencies.length, state.currentPage, state.itemsPerPage]);

  // Get current page travel agencies
  const currentPageTravelAgencies = useMemo(() => {
    const startIndex = (state.currentPage - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage;
    return filteredTravelAgencies.slice(startIndex, endIndex);
  }, [filteredTravelAgencies, state.currentPage, state.itemsPerPage]);

  const handleSearchChange = (searchTerm: string) => {
    setState(prev => ({
      ...prev,
      searchTerm,
      currentPage: 1 // Reset to first page when searching
    }));
  };

  const handlePageChange = (page: number) => {
    setState(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleCreateNew = () => {
    alert('Create New Travel Agency Modal\n\nThis would open a form with:\n- TA Code input\n- TA Name input\n- Status configuration\n- Account activation options');
  };

  const handleViewTravelAgency = (travelAgency: TravelAgency) => {
    setViewDetailsModal({ isOpen: true, travelAgency });
  };

  const handleEditTravelAgency = (travelAgency: TravelAgency) => {
    if (onEditTravelAgency) {
      onEditTravelAgency(travelAgency);
    } else {
      alert(`Edit Travel Agency Modal\n\nThis would open an edit form for:\n- TA Code: ${travelAgency.taCode}\n- TA Name: ${travelAgency.taName}\n- Status modifications`);
    }
  };

  const handleDeleteTravelAgency = (travelAgency: TravelAgency) => {
    const confirmDelete = confirm(`Are you sure you want to delete travel agency "${travelAgency.taName}" (${travelAgency.taCode})?\n\nThis action cannot be undone.`);
    if (confirmDelete) {
      setState(prev => ({
        ...prev,
        travelAgencies: prev.travelAgencies.filter(ta => ta.id !== travelAgency.id),
        totalTravelAgencies: prev.totalTravelAgencies - 1
      }));
      alert(`Travel Agency "${travelAgency.taName}" has been deleted successfully.`);
    }
  };

  const renderPagination = () => {
    return (
      <div className="travel-agency-pagination">
        <div className="pagination-info">
          Showing {paginationInfo.startTravelAgency} to {paginationInfo.endTravelAgency} of {paginationInfo.totalTravelAgencies} travel agencies
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage - 1)}
            disabled={paginationInfo.currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-number ${page === paginationInfo.currentPage ? 'active' : ''}`}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </button>
          ))}
          
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(paginationInfo.currentPage + 1)}
            disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`travel-agency-maintenance ${className}`}>
      {/* Header */}
      <div className="travel-agency-maintenance-header">
        <div className="travel-agency-maintenance-header-left">
          <div className="travel-agency-maintenance-icon">
            <Building2 size={24} />
          </div>
          <div className="travel-agency-maintenance-title-section">
            <h1 className="travel-agency-maintenance-title">Manage Travel Agency</h1>
            <div className="travel-agency-maintenance-context">
              Manage and oversee travel agency records and activities
            </div>
          </div>
        </div>
        
        <div className="travel-agency-maintenance-header-actions">
          <button
            className="btn-create-new-travel-agency"
            onClick={handleCreateNew}
          >
            <Plus size={16} />
            Create New
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="travel-agency-maintenance-search-section">
        <div className="travel-agency-search-controls">
          <div className="travel-agency-search-input-container">
            <Search size={16} className="travel-agency-search-icon" />
            <input
              type="text"
              placeholder="Search by TA code or TA name..."
              value={state.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="travel-agency-search-input"
            />
            <div className="travel-agency-search-tooltip">
              Search by TA code or TA name. Type to filter results instantly.
            </div>
          </div>
        </div>
        
        <div className="travel-agency-count-info">
          <Users size={16} className="travel-agency-count-icon" />
          <span className="travel-agency-count-text">{filteredTravelAgencies.length} travel agencies found</span>
        </div>
      </div>

      {/* Table */}
      <div className="travel-agency-table-container">
        <table className="travel-agency-table">
          <thead className="travel-agency-table-header">
            <tr>
              <th className="ta-code-column">TA CODE</th>
              <th className="ta-name-column">TA NAME</th>
              <th className="is-active-column">IS ACTIVE</th>
              <th className="actions-column">ACTIONS</th>
            </tr>
          </thead>
          <tbody className="travel-agency-table-body">
            {currentPageTravelAgencies.map((travelAgency) => (
              <tr key={travelAgency.id} className="travel-agency-row">
                <td className="ta-code-cell">{travelAgency.taCode}</td>
                <td className="ta-name-cell">{travelAgency.taName}</td>
                <td className="is-active-cell">
                  <div className={`status-indicator ${travelAgency.isActive ? 'active' : 'inactive'}`}>
                    <div className={`status-dot ${travelAgency.isActive ? 'active' : 'inactive'}`}></div>
                    {travelAgency.isActive ? 'Active' : 'Inactive'}
                  </div>
                </td>
                <td>
                  <div className="travel-agency-action-buttons">
                    <button
                      className="travel-agency-action-btn view"
                      onClick={() => handleViewTravelAgency(travelAgency)}
                      title="View Travel Agency"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      className="travel-agency-action-btn edit"
                      onClick={() => handleEditTravelAgency(travelAgency)}
                      title="Edit Travel Agency"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="travel-agency-action-btn delete"
                      onClick={() => handleDeleteTravelAgency(travelAgency)}
                      title="Delete Travel Agency"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Travel Agency Details Modal */}
      <TravelAgencyDetailsModal
        isOpen={viewDetailsModal.isOpen}
        onClose={() => setViewDetailsModal({ isOpen: false, travelAgency: null })}
        travelAgency={viewDetailsModal.travelAgency}
        onEdit={(travelAgency: TravelAgency) => {
          setViewDetailsModal({ isOpen: false, travelAgency: null });
          handleEditTravelAgency(travelAgency);
        }}
      />
    </div>
  );
};

export default TravelAgencyMaintenance;